/**
 * The setup forced at sign-in (a 202 `mfa_enrollment_required` from POST /v2/edit/login/ on a tenant that requires
 * two-step sign-in; apiv2/dashboard_auth.openapi.yaml `mfa/enroll/…` with `X-Enrollment-Token`). Rendered inside
 * SignIn. Ported from the console's MFAEnrollPage.
 *
 * Authenticator app (recommended): the backend's QR code (an SVG in an <img>) and the key to type in by hand, then
 * the first code. Text message: a mobile number (the tenant's country preselected), Send / Resend, then the texted
 * code. The confirm answers the session and the ten recovery codes together (`onEnrolled`); SignIn shows the codes
 * before it signs in.
 */
import { Alert, Box, Button, Link, Stack, Typography } from '@mui/material';
import MaterialUiPhoneNumber from 'material-ui-phone-number';
import React from 'react';

import { SIGN_IN_UNREACHABLE } from '../store/contextStore';
import { confirmEnrollment, describeRefusal, startEnrollment } from './mfaApi';
import { CodeField, codeReady, useCountdown } from './mfaUi';

export const APP_LABEL = 'Authenticator app (recommended)';
export const SMS_LABEL = 'Text message';

// A typed number worth sending: a handful of digits beyond the preselected country prefix.
const phoneReady = (phone) => (phone || '').replace(/\D/g, '').length >= 7;

export function TotpSetup({ device }) {
    return (
        <Box sx={{ mt: 1 }}>
            <Typography variant="body2">1. Scan this QR code with your authenticator app.</Typography>
            <Box sx={{ textAlign: 'center', my: 1 }}>
                <img src={`data:image/svg+xml;base64,${device.qr}`} alt="QR code for your authenticator app" width={200} height={200} />
            </Box>
            <Typography variant="body2" color="text.secondary">
                Cannot scan it? Enter this key in the app:{' '}
                <Box component="code" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>{device.secret}</Box>
            </Typography>
            <Typography variant="body2" sx={{ mt: 1 }}>2. Enter the 6-digit code the app shows.</Typography>
        </Box>
    );
}

export function PhoneField({ region, value, onChange, error, disabled }) {
    return (
        <MaterialUiPhoneNumber
            fullWidth
            variant="outlined"
            margin="normal"
            label="Mobile number"
            defaultCountry={region ? region.toLowerCase() : undefined}
            value={value}
            onChange={onChange}
            disabled={disabled}
            error={!!error}
            helperText={error || 'We text a 6-digit code to this number at every sign-in.'}
        />
    );
}

export default function MfaEnrollStep({ enrollment, onEnrolled, onRestart }) {
    const token = enrollment.mfa_enrollment_token;
    const [kind, setKind] = React.useState(null);
    // The answer of mfa/enroll/{kind}/: the unconfirmed device the code confirms.
    const [device, setDevice] = React.useState(null);
    const [phone, setPhone] = React.useState('');
    const [phoneError, setPhoneError] = React.useState(null);
    const [code, setCode] = React.useState('');
    const [busy, setBusy] = React.useState(false);
    const [error, setError] = React.useState(null);
    // The enrollment token is gone: nothing works but "Start again".
    const [spent, setSpent] = React.useState(false);
    const [resendIn, startResend] = useCountdown();

    const start = async (which) => {
        setBusy(true);
        setError(null);
        setPhoneError(null);
        try {
            const { status, body } = await startEnrollment(token, which, which === 'sms' ? phone : undefined);
            if (status === 200 || status === 201) {
                setDevice(body);
                setCode('');
                if (which === 'sms') {
                    startResend(body?.resend_after);
                }
            } else {
                if (status === 401) {
                    setSpent(true);
                }
                if (body?.code === 'sms_cooldown') {
                    startResend(body.retry_after);
                }
                if (body?.code === 'invalid_phone') {
                    setPhoneError(describeRefusal(body));
                } else {
                    setError(describeRefusal(body, 'The setup could not start. Try again.'));
                }
            }
        } catch (err) {
            setError(SIGN_IN_UNREACHABLE);
        }
        setBusy(false);
    };

    const choose = (which) => {
        setKind(which);
        setDevice(null);
        setError(null);
        if (which === 'totp') {
            start('totp');
        }
    };

    const confirm = async (ev) => {
        ev.preventDefault();
        if (busy || spent || !device || !codeReady(code)) {
            return;
        }
        setBusy(true);
        setError(null);
        let answer;
        try {
            answer = await confirmEnrollment(token, kind, device.device_id, code);
        } catch (err) {
            setError(SIGN_IN_UNREACHABLE);
            setBusy(false);
            return;
        }
        const { status, body } = answer;
        if (status === 201 || status === 200) {
            onEnrolled(body);
            return;
        }
        if (status === 401) {
            setSpent(true);
        }
        // Five wrong codes delete the pending device: this step starts over (a new QR code, or Send again).
        if (status === 404 || body?.attempts_left === 0) {
            setDevice(null);
        }
        setCode('');
        setError(describeRefusal(body, 'The code could not be checked. Try again.'));
        setBusy(false);
    };

    let sendLabel = device ? 'Resend code' : 'Send code';
    if (resendIn > 0) {
        sendLabel = `${device ? 'Resend' : 'Send'} in ${resendIn} s`;
    }

    return (
        <Box component="form" noValidate onSubmit={confirm} sx={{ width: '100%', mt: 2 }}>
            <Typography variant="h6" component="h2">Set up two-step sign-in</Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Your company requires a second step at every password sign-in: a code from an app on your phone, or one
                we text you.
            </Typography>

            {!kind && (
                <Stack spacing={1} sx={{ mt: 2 }}>
                    <Button variant="contained" color="primary" onClick={() => choose('totp')}>{APP_LABEL}</Button>
                    <Button variant="outlined" color="primary" onClick={() => choose('sms')}>{SMS_LABEL}</Button>
                </Stack>
            )}

            {kind === 'totp' && device && <TotpSetup device={device} />}
            {kind === 'totp' && !device && !busy && (
                <Button variant="outlined" sx={{ mt: 2 }} onClick={() => start('totp')} disabled={spent}>Get a new QR code</Button>
            )}

            {kind === 'sms' && (
                <>
                    <PhoneField region={enrollment.phone_region} value={phone} onChange={setPhone} error={phoneError} disabled={busy || spent || !!device} />
                    {device && (
                        <Typography variant="body2" color="text.secondary">
                            We sent a code to {device.sent_to_masked}. It works for 5 minutes.{' '}
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => {
                                    setDevice(null);
                                    startResend(0);
                                }}
                            >
                                Use a different number
                            </Link>
                        </Typography>
                    )}
                    <Button variant="outlined" sx={{ mt: 1 }} onClick={() => start('sms')} disabled={busy || spent || resendIn > 0 || !phoneReady(phone)}>
                        {sendLabel}
                    </Button>
                </>
            )}

            {device && <CodeField value={code} onChange={setCode} autoFocus disabled={spent} />}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
            {device && (
                <Button type="submit" fullWidth variant="contained" color="primary" sx={{ mt: 2 }} disabled={busy || spent || !codeReady(code)}>
                    Confirm
                </Button>
            )}

            <Stack direction="row" justifyContent="space-between" sx={{ mt: 1 }}>
                {kind ? (
                    <Link component="button" type="button" variant="body2" onClick={() => choose(null)} disabled={busy}>
                        Choose another method
                    </Link>
                ) : <span />}
                <Link component="button" type="button" variant="body2" onClick={onRestart}>Start again</Link>
            </Stack>
        </Box>
    );
}
